"use client";

import { useEffect, useState } from "react";
import { apiPost } from "@/lib/api";
import { useAlbums } from "@/lib/album-context";
import { useToast } from "@/components/toast-provider";
import * as s from "@/lib/styles";

interface Props {
  open: boolean;
  /** Photos to share — only ones the caller owns should be passed in. */
  photoIds: string[];
  onClose: () => void;
  onShared: () => void;
}

/**
 * Pick which albums to share the selected photos into. Destinations are any
 * album the user can contribute to: their own, plus shared albums where the
 * owner granted contribute rights.
 */
export default function SharePhotosModal({ open, photoIds, onClose, onShared }: Props) {
  const { showToast } = useToast();
  const { own, shared, current } = useAlbums();
  const [targets, setTargets] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setTargets(new Set());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const candidates = [...own, ...shared].filter(
    (a) => a.canContribute && a.id !== current,
  );

  function toggle(id: number) {
    setTargets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (targets.size === 0 || photoIds.length === 0) return;
    setBusy(true);
    let shareCount = 0;
    try {
      for (const photoId of photoIds) {
        const res = await apiPost<{ shared: number }>(`/photos/${photoId}/share`, {
          albumIds: [...targets],
        });
        shareCount += res.shared ?? 0;
      }
      showToast(
        "success",
        `Shared ${photoIds.length} photo${photoIds.length === 1 ? "" : "s"} into ` +
          `${targets.size} album${targets.size === 1 ? "" : "s"}` +
          (shareCount === 0 ? " (already there)" : ""),
      );
      onShared();
      onClose();
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Share failed");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className={s.modalOverlay + " z-50 p-4"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-photos-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className={s.modalContent}>
        <h2 id="share-photos-title" className="text-base font-semibold text-purple m-0">
          Share {photoIds.length} photo{photoIds.length === 1 ? "" : "s"}
        </h2>

        {candidates.length === 0 ? (
          <p className="text-sm text-muted m-0">
            There is nowhere to share these yet. Create another album, or ask
            someone to give you contribute access to theirs.
          </p>
        ) : (
          <ul className="list-none p-0 m-0 max-h-64 overflow-y-auto space-y-1">
            {candidates.map((a) => (
              <li key={a.id}>
                <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-light cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={targets.has(a.id)}
                    onChange={() => toggle(a.id)}
                  />
                  <span className="truncate">{a.name}</span>
                  {!a.owned && (
                    <span className="text-[10px] text-muted shrink-0">
                      shared by {a.owner}
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}

        <p className="text-[11px] text-muted m-0 leading-relaxed">
          Sharing links the photo into another album — it does not copy it. You
          stay the owner, and only you can delete it.
        </p>

        <div className="flex justify-end gap-2">
          <button type="button" className={s.btnAuxiliary} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={() => void submit()}
            disabled={busy || targets.size === 0}
          >
            {busy ? "Sharing…" : "Share"}
          </button>
        </div>
      </div>
    </div>
  );
}
